import { AlertCircle, Check, CheckCircle2, ChevronLeft, ChevronRight, GripVertical, Info, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AssetId } from "../content/assetManifest";
import { AssetImage } from "./AssetImage";

export interface DragItem {
  id: string;
  label: string;
  icon?: string;
  assetId?: AssetId;
  targetId: string;
}

export interface DragTarget {
  id: string;
  label: string;
  hint?: string;
  assetId?: AssetId;
}

interface DragBoardProps {
  items: DragItem[];
  targets: DragTarget[];
  onAttempt: (correct: boolean, isFirstTry: boolean) => void;
  onComplete: () => void;
  onItemAttempt?: (itemId: string, targetId: string, correct: boolean, isFirstTry: boolean) => void;
  onAssignedChange?: (assigned: Record<string, string>) => void;
  onReset?: () => void;
  initialAssigned?: Record<string, string>;
  compact?: boolean;
  pagination?: {
    pageSize: number;
  };
  targetPagination?: {
    pageSize: number;
  };
  itemSectionLabel?: string;
  targetSectionLabel?: string;
  locked?: boolean;
  lockedMessage?: string;
  onTargetInspect?: (target: DragTarget) => void;
}

type DragFeedback = { kind: "info" | "error" | "success"; message: string };

export function getDragBoardPage(remainingCount: number, requestedPageIndex: number, requestedPageSize: number) {
  const pageSize = Math.max(1, Math.floor(requestedPageSize));
  const itemCount = Math.max(0, Math.floor(remainingCount));
  const pageCount = Math.ceil(itemCount / pageSize);
  const pageIndex = pageCount === 0
    ? 0
    : Math.min(Math.max(0, Math.floor(requestedPageIndex)), pageCount - 1);
  const startIndex = pageIndex * pageSize;
  const visibleCount = Math.min(pageSize, Math.max(0, itemCount - startIndex));

  return {
    pageSize,
    pageCount,
    pageIndex,
    startIndex,
    endIndex: startIndex + pageSize,
    emptySlotCount: pageCount === 0 ? 0 : pageSize - visibleCount
  };
}

// 通用拖拽板同时支持鼠标拖放与“点选卡片，再点目标”的触控替代。
export function DragBoard({
  items,
  targets,
  onAttempt,
  onComplete,
  onItemAttempt,
  onAssignedChange,
  onReset,
  initialAssigned = {},
  compact = false,
  pagination,
  targetPagination,
  itemSectionLabel,
  targetSectionLabel,
  locked = false,
  lockedMessage = "请先完成右侧任务，再开始匹配。",
  onTargetInspect
}: DragBoardProps) {
  const [assigned, setAssigned] = useState<Record<string, string>>(initialAssigned);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [attemptedIds, setAttemptedIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<DragFeedback>({ kind: "info", message: "可拖动，也可先点卡片再点目标。" });
  const [errorItemId, setErrorItemId] = useState<string | null>(null);
  const [errorTargetId, setErrorTargetId] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [targetPageIndex, setTargetPageIndex] = useState(0);
  const [filledItemId, setFilledItemId] = useState<string | null>(null);

  const remaining = useMemo(() => items.filter((item) => !assigned[item.id]), [assigned, items]);
  const page = pagination ? getDragBoardPage(remaining.length, pageIndex, pagination.pageSize) : null;
  const visibleItems = page ? remaining.slice(page.startIndex, page.endIndex) : remaining;
  const effectivePageIndex = page?.pageIndex;
  const targetPage = targetPagination ? getDragBoardPage(targets.length, targetPageIndex, targetPagination.pageSize) : null;
  const visibleTargets = targetPage ? targets.slice(targetPage.startIndex, targetPage.endIndex) : targets;
  const effectiveTargetPageIndex = targetPage?.pageIndex;

  useEffect(() => {
    if (effectivePageIndex !== undefined && pageIndex !== effectivePageIndex) setPageIndex(effectivePageIndex);
  }, [effectivePageIndex, pageIndex]);

  useEffect(() => {
    if (effectiveTargetPageIndex !== undefined && targetPageIndex !== effectiveTargetPageIndex) setTargetPageIndex(effectiveTargetPageIndex);
  }, [effectiveTargetPageIndex, targetPageIndex]);

  // 校验放置目标，错误时保留卡片位置并给出观察提示。
  function placeItem(itemId: string, targetId: string) {
    if (locked) return;
    const item = items.find((candidate) => candidate.id === itemId);
    const target = targets.find((candidate) => candidate.id === targetId);
    if (!item || !target || assigned[itemId]) return;
    const firstTry = !attemptedIds.has(itemId);
    const correct = item.targetId === targetId;
    onAttempt(correct, firstTry);
    onItemAttempt?.(itemId, targetId, correct, firstTry);
    setAttemptedIds((current) => new Set(current).add(itemId));

    if (!correct) {
      setFeedback({ kind: "error", message: `“${item.label}”与“${target?.label ?? targetId}”不匹配。卡片已回到待放区，请核对两者的关系后重试。` });
      setErrorItemId(itemId);
      setErrorTargetId(targetId);
      setSelectedId(null);
      return;
    }

    const nextAssigned = { ...assigned, [itemId]: targetId };
    const nextRemainingCount = remaining.length - 1;
    const nextFillItemId = page && remaining.length > page.endIndex
      ? remaining[page.endIndex]?.id ?? null
      : null;
    setAssigned(nextAssigned);
    onAssignedChange?.(nextAssigned);
    setSelectedId(null);
    setErrorItemId(null);
    setErrorTargetId(null);
    setFilledItemId(nextFillItemId);
    setFeedback({
      kind: "success",
      message: pagination && nextRemainingCount > 0
        ? nextFillItemId
          ? `“${item.label}”已放置，下一张卡片已补入；还剩 ${nextRemainingCount} 张。`
          : `“${item.label}”已放置；还剩 ${nextRemainingCount} 张。`
        : `“${item.label}”放置正确。`
    });
    if (nextRemainingCount === 0) {
      onComplete();
    }
  }

  // 重置本轮摆放，不清除全局诊断记录。
  function resetBoard() {
    if (locked) return;
    setAssigned({});
    onAssignedChange?.({});
    onReset?.();
    setSelectedId(null);
    setErrorItemId(null);
    setErrorTargetId(null);
    setPageIndex(0);
    setTargetPageIndex(0);
    setFilledItemId(null);
    setFeedback({ kind: "info", message: "卡片已全部移回待放区，可以重新放置。重置不会清除已记录的第一次放置结果。" });
  }

  function changeItemPage(nextPageIndex: number) {
    if (locked) return;
    if (!page || nextPageIndex === page.pageIndex || nextPageIndex < 0 || nextPageIndex >= page.pageCount) return;
    setPageIndex(nextPageIndex);
    setSelectedId(null);
    setErrorItemId(null);
    setErrorTargetId(null);
    setFilledItemId(null);
    setFeedback({ kind: "info", message: `已切换到第 ${nextPageIndex + 1} 页，请选择一张卡片。` });
  }

  function changeTargetPage(nextPageIndex: number) {
    if (locked) return;
    if (!targetPage || nextPageIndex === targetPage.pageIndex || nextPageIndex < 0 || nextPageIndex >= targetPage.pageCount) return;
    setTargetPageIndex(nextPageIndex);
    setErrorTargetId(null);
    setFeedback(selectedId
      ? { kind: "info", message: `已切换目标图片到第 ${nextPageIndex + 1} 页。“${items.find((item) => item.id === selectedId)?.label ?? "所选卡片"}”仍保持选中。` }
      : { kind: "info", message: `已切换目标图片到第 ${nextPageIndex + 1} 页。` });
  }

  function toggleItemSelection(item: DragItem) {
    if (locked) return;
    const nextSelected = selectedId === item.id ? null : item.id;
    setSelectedId(nextSelected);
    setErrorItemId(null);
    setErrorTargetId(null);
    setFeedback(nextSelected
      ? { kind: "info", message: `已选中“${item.label}”。现在点击一个目标完成放置。` }
      : { kind: "info", message: "已取消选择。可重新选择一张卡片。" });
  }

  const visibleFeedback = locked ? { kind: "info" as const, message: lockedMessage } : feedback;
  const FeedbackIcon = visibleFeedback.kind === "error" ? AlertCircle : visibleFeedback.kind === "success" ? CheckCircle2 : Info;
  const dualPagination = Boolean(targetPagination);

  return (
    <div
      className={`drag-board ${compact ? "drag-board--compact" : ""} ${pagination ? "drag-board--paginated" : ""} ${dualPagination ? "drag-board--dual-paginated" : ""} ${locked ? "is-locked" : ""}`}
      data-current-page={page ? page.pageIndex + 1 : undefined}
      data-page-count={page?.pageCount}
      data-target-current-page={targetPage ? targetPage.pageIndex + 1 : undefined}
      data-target-page-count={targetPage?.pageCount}
      aria-label={locked ? lockedMessage : undefined}
    >
      <div className="drag-board__toolbar">
        <div
          className={`drag-board__feedback drag-board__feedback--${visibleFeedback.kind}`}
          data-feedback-kind={visibleFeedback.kind}
          role={visibleFeedback.kind === "error" ? "alert" : "status"}
        >
          <FeedbackIcon size={17} aria-hidden="true" />
          <span>{visibleFeedback.message}</span>
        </div>
        <button className="icon-button" type="button" onClick={resetBoard} title="重置卡片" aria-label="重置卡片" disabled={locked}>
          <RotateCcw size={18} />
        </button>
      </div>
      {itemSectionLabel && <h3 className="drag-board__section-title">{itemSectionLabel}</h3>}
      <div className="drag-board__items" aria-label="待放置卡片">
        {visibleItems.map((item) => (
          <button
            className={`drag-card ${selectedId === item.id ? "is-selected" : ""} ${errorItemId === item.id ? "is-error" : ""} ${filledItemId === item.id ? "is-filled" : ""}`}
            key={item.id}
            type="button"
            draggable={!locked}
            disabled={locked}
            aria-pressed={selectedId === item.id}
            aria-label={`${item.label}，${selectedId === item.id ? "已选中，请选择放置目标" : "可拖动或点选"}`}
            onDragStart={(event) => {
              if (!locked) event.dataTransfer.setData("text/plain", item.id);
            }}
            onClick={() => toggleItemSelection(item)}
            onAnimationEnd={() => {
              if (filledItemId === item.id) setFilledItemId(null);
            }}
          >
            <GripVertical size={16} aria-hidden="true" />
            {item.assetId && <AssetImage assetId={item.assetId} className="drag-card__media" sizes="160px" alt="" eager />}
            {!item.assetId && item.icon && <span className="drag-card__icon">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))}
        {page && Array.from({ length: page.emptySlotCount }, (_, index) => (
          <div className="drag-board__empty-slot" aria-hidden="true" key={`empty-slot-${index}`} />
        ))}
        {remaining.length === 0 && (
          <p className="drag-board__empty" role="status">全部卡片已放置。可核对下方结果，或使用重置重新尝试。</p>
        )}
      </div>
      {page && page.pageCount > 0 && (
        <nav className="drag-board__pagination" aria-label="待放置卡片分页">
          <button
            type="button"
            disabled={locked || page.pageIndex === 0}
            onClick={() => changeItemPage(page.pageIndex - 1)}
          >
            <ChevronLeft size={17} aria-hidden="true" />
            上一页
          </button>
          <span aria-live="polite" aria-atomic="true">第 {page.pageIndex + 1} / {page.pageCount} 页</span>
          <button
            type="button"
            disabled={locked || page.pageIndex === page.pageCount - 1}
            onClick={() => changeItemPage(page.pageIndex + 1)}
          >
            下一页
            <ChevronRight size={17} aria-hidden="true" />
          </button>
        </nav>
      )}
      {targetSectionLabel && <h3 className="drag-board__section-title drag-board__section-title--targets">{targetSectionLabel}</h3>}
      <div className="drag-board__targets" aria-label="目标图片">
        {visibleTargets.map((target) => {
          const placed = items.filter((item) => assigned[item.id] === target.id);
          const complete = placed.length > 0;
          return (
            <button
              className={`drop-target ${selectedId && !complete ? "is-ready" : ""} ${errorTargetId === target.id ? "is-error" : ""} ${complete ? "is-complete" : ""}`}
              key={target.id}
              type="button"
              disabled={locked}
              aria-label={`${target.label}${selectedId ? "，放置选中的卡片" : complete ? "，已完成，打开大图" : onTargetInspect ? "，打开大图" : ""}`}
              onDragOver={(event) => {
                if (!locked) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (locked) return;
                placeItem(event.dataTransfer.getData("text/plain"), target.id);
              }}
              onClick={() => {
                if (locked) return;
                if (selectedId) {
                  placeItem(selectedId, target.id);
                  return;
                }
                if (complete) {
                  onTargetInspect?.(target);
                  return;
                }
                if (onTargetInspect) {
                  onTargetInspect(target);
                  return;
                }
                setErrorItemId(null);
                setErrorTargetId(target.id);
                setFeedback({ kind: "error", message: `请先选择一张待放卡片，再放入“${target.label}”。` });
              }}
            >
              {target.assetId && <AssetImage assetId={target.assetId} className="drop-target__media" sizes="220px" alt="" eager />}
              <strong>{target.label}</strong>
              {target.hint && <small>{target.hint}</small>}
              <span className="drop-target__placed">
                {placed.map((item) => <span key={item.id}><Check size={13} /> {item.label}</span>)}
              </span>
            </button>
          );
        })}
        {targetPage && Array.from({ length: targetPage.emptySlotCount }, (_, index) => (
          <div className="drag-board__target-empty-slot" aria-hidden="true" key={`target-empty-slot-${index}`} />
        ))}
      </div>
      {targetPage && targetPage.pageCount > 0 && (
        <nav className="drag-board__pagination drag-board__pagination--targets" aria-label="目标图片分页">
          <button
            type="button"
            disabled={locked || targetPage.pageIndex === 0}
            onClick={() => changeTargetPage(targetPage.pageIndex - 1)}
          >
            <ChevronLeft size={17} aria-hidden="true" />
            上一页
          </button>
          <span aria-live="polite" aria-atomic="true">第 {targetPage.pageIndex + 1} / {targetPage.pageCount} 页</span>
          <button
            type="button"
            disabled={locked || targetPage.pageIndex === targetPage.pageCount - 1}
            onClick={() => changeTargetPage(targetPage.pageIndex + 1)}
          >
            下一页
            <ChevronRight size={17} aria-hidden="true" />
          </button>
        </nav>
      )}
    </div>
  );
}
